import { Box, IconButton, Stack } from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { Position } from '@xyflow/react';

import { SpanWithEllipsisAndTooltipOnOverflow, colors, isDefined } from 'common';

import { NodeHandle } from './Handle';
import {
  EdgeHandleType,
  getSourceEdgeHandleType,
  isPartialBlockNodeWithData,
} from '../../../../../types';
import { BlockExecutionState, type ReactFlowBlockTypeData } from '../../../../types';
import { NodeHandleNameOverflowVariant, type NodeHandleProps } from '../../../types';
import { type ReactFlowBlockNodeProps, type ReactFlowPlaceholderNodeProps } from '../types';

interface NodeNameAndHandleProps {
  nodeProps: ReactFlowBlockNodeProps | ReactFlowPlaceholderNodeProps;
  nodeHandle: NodeHandleProps;
  onClose?: () => void;
}

export function NodeNameAndHandle(props: NodeNameAndHandleProps) {
  const { nodeProps, nodeHandle, onClose } = props;
  const { type, id, handleName } = nodeHandle;
  const edgeHandleType = getSourceEdgeHandleType(id);
  let doesHandleMatchReturnValue = false;
  if (isPartialBlockNodeWithData(nodeProps)) {
    const { run } = nodeProps.data;
    if (isDefined(run)) {
      if (run.state === BlockExecutionState.Failed && edgeHandleType === EdgeHandleType.Error) {
        doesHandleMatchReturnValue = true;
      } else if (
        run.state === BlockExecutionState.Completed &&
        edgeHandleType === EdgeHandleType.Default
      ) {
        doesHandleMatchReturnValue = isMatchedCondition(nodeHandle.id, run);
      }
    }
  }

  const isDefault = edgeHandleType === EdgeHandleType.Default;
  const labelText = isDefault ? (handleName ?? id) : 'Error';
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box paddingX={2} width="100%">
        <Box
          sx={{
            paddingX: 2,
            paddingY: 1,
            backgroundColor: doesHandleMatchReturnValue
              ? isDefault
                ? colors.extended.green[95]
                : colors.extended.red[95]
              : colors.extended.gray[97],
            fontWeight: doesHandleMatchReturnValue ? 'bold' : undefined,
            overflowX: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            {nodeHandle.nameWrapVariant === NodeHandleNameOverflowVariant.Ellipsis ? (
              <SpanWithEllipsisAndTooltipOnOverflow>
                {labelText}
              </SpanWithEllipsisAndTooltipOnOverflow>
            ) : (
              labelText
            )}
            {isDefined(onClose) && (
              <IconButton
                aria-label="Hide Extra Target Handles"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onClose();
                }}
              >
                <XIcon />
              </IconButton>
            )}
          </Stack>
        </Box>
      </Box>
      <NodeHandle
        id={id}
        type={type}
        position={type === 'target' ? Position.Left : Position.Right}
        nodeProps={nodeProps}
        edgeHandleType={edgeHandleType}
      />
    </Box>
  );
}
// Keep in sync with isMatchedCondition in backend/flow/api
function isMatchedCondition(
  conditionalValue: string | undefined,
  run: NonNullable<ReactFlowBlockTypeData['run']>
): boolean {
  if (!conditionalValue) {
    return !run.returnValue;
  }

  if (
    run.metadata &&
    typeof run.metadata === 'object' &&
    'satisfiedConditions' in run.metadata &&
    Array.isArray(run.metadata.satisfiedConditions)
  ) {
    // If a block records satisfiedConditions, that's the source of truth for if a condition matches
    return run.metadata.satisfiedConditions.includes(conditionalValue);
  }

  // This is kept as a fallback to maintain backwards compatibility.
  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(conditionalValue);
  } catch (outerError) {
    try {
      parsedValue = JSON.parse(`"${conditionalValue}"`);
    } catch (error) {
      throw new Error(`Invalid conditional value: ${conditionalValue}`);
    }
  }
  return parsedValue === run.returnValue;
}
